import { useState } from "react";
import { useTranslation } from "react-i18next";
import SEO from "./common/SEO";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHandHoldingHeart,
  faCopy,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";

const Support = ({ sponsors }) => {
  const { i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const isEnglish = (i18n.language || "it").startsWith("en");
  const title = isEnglish ? "Support & Partnerships" : "Sostegno & Collaborazione";
  const desc = isEnglish
    ? "Support the young musicians and artistic activities of Nuova Orchestra da Camera Ferruccio Busoni (5x1000 Runts CF 90090300329)."
    : "Sostieni i giovani musicisti e l'attività artistica della Nuova Orchestra da Camera Ferruccio Busoni (dona il 5x1000 C.F. 90090300329).";

  const FISCAL_CODE = "90090300329";

  const handleCopyCF = () => {
    navigator.clipboard.writeText(FISCAL_CODE).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <>
      <SEO title={title} description={desc} />
      <section className="section subpage-section">
        <div className="container subpage-container">
          {/* 5x1000 Support Hero Card */}
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-5 support-hero-card">
            <div className="p-4 p-md-5 bg-navy-support text-white">
              <div className="row align-items-center g-4">
                <div className="col-lg-8">
                  <h2 className="text-white fw-bold mb-3">
                    {isEnglish
                      ? "Support the Ferruccio Busoni Chamber Orchestra"
                      : "Sostieni la Nuova Orchestra da Camera Ferruccio Busoni"}
                  </h2>
                  <p className="text-light opacity-90 mb-4" style={{ lineHeight: "1.7" }}>
                    {isEnglish
                      ? "The Ferruccio Busoni Orchestra is registered in RUNTS (Single National Register of the Third Sector). By donating your 5x1000, you support young emerging musicians, masterclasses, and prestigious international concert seasons. It costs you nothing extra!"
                      : "L'Orchestra Ferruccio Busoni è iscritta al RUNTS nella categoria 'Altri enti del Terzo Settore'. Donando il tuo 5x1000 sosterrai i giovani musicisti dell'orchestra, borse di studio e la nostra attività concertistica. Non costa nulla!"}
                  </p>

                  {/* Fiscal Code Box */}
                  <div className="cf-action-box d-inline-flex align-items-center p-3 rounded-3 flex-wrap gap-3">
                    <div>
                      <small className="cf-label d-block text-uppercase fw-semibold">
                        {isEnglish ? "Tax / Fiscal Code" : "Codice Fiscale dell'Orchestra"}
                      </small>
                      <span className="cf-number fs-4 fw-bold font-monospace letter-spacing-1">
                        {FISCAL_CODE}
                      </span>
                    </div>

                    <button
                      type="button"
                      className={`btn btn-sm ${
                        copied ? "btn-success" : "btn-warning"
                      } rounded-pill px-3 py-2 fw-bold text-dark`}
                      onClick={handleCopyCF}
                    >
                      <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="me-2" />
                      {copied
                        ? isEnglish
                          ? "Copied to Clipboard!"
                          : "Copiato negli appunti!"
                        : isEnglish
                        ? "Copy Fiscal Code"
                        : "Copia Codice Fiscale"}
                    </button>
                  </div>
                </div>

                <div className="col-lg-4 text-center d-none d-lg-block">
                  <div className="support-illustration-circle mx-auto p-4 rounded-circle bg-white bg-opacity-10">
                    <FontAwesomeIcon icon={faHandHoldingHeart} className="text-warning display-1" />
                  </div>
                </div>
              </div>
            </div>

            {/* How to Donate 3 Steps */}
            <div className="bg-light p-4 p-md-4 border-top">
              <div className="row g-3 text-center">
                <div className="col-md-4">
                  <div className="p-3 bg-white rounded-3 shadow-sm h-100">
                    <div className="step-num-badge mb-2">1</div>
                    <h6 className="fw-bold mb-1">{isEnglish ? "Sign the Form" : "Firma la Casella"}</h6>
                    <small className="text-muted">
                      {isEnglish
                        ? "Sign in the 'Sostegno del Terzo Settore' section of your tax return."
                        : "Firma nel riquadro 'Sostegno degli enti del Terzo settore' della dichiarazione dei redditi."}
                    </small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 bg-white rounded-3 shadow-sm h-100">
                    <div className="step-num-badge mb-2">2</div>
                    <h6 className="fw-bold mb-1">{isEnglish ? "Enter the Code" : "Inserisci il Codice"}</h6>
                    <small className="text-muted">
                      {isEnglish
                        ? `Enter the fiscal code: ${FISCAL_CODE}.`
                        : `Trascrivi il codice fiscale: ${FISCAL_CODE}.`}
                    </small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 bg-white rounded-3 shadow-sm h-100">
                    <div className="step-num-badge mb-2">3</div>
                    <h6 className="fw-bold mb-1">{isEnglish ? "Transformed into Music" : "Diventa Musica"}</h6>
                    <small className="text-muted">
                      {isEnglish
                        ? "Your contribution is turned into live concerts and masterclasses for youth!"
                        : "Il tuo contributo viene trasformato in concerti ed eccellenza per i nostri giovani!"}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section: Institutional Partners & Sponsors */}
          <div className="sponsors-section pt-4">
            <div className="text-center mb-5">
              <h2 className="subpage-title">
                {isEnglish ? "Institutional Partners & Sponsors" : "I Nostri Sostenitori & Istituzioni Partner"}
              </h2>
              <p className="text-muted max-w-700 mx-auto mt-2">
                {isEnglish
                  ? "We warmly thank all public institutions, foundations, and private partners who make our musical and cultural activities possible."
                  : "Ringraziamo sentitamente gli enti pubblici, le fondazioni benefiche e le istituzioni che sostengono la nostra attività musicale e culturale."}
              </p>
            </div>

            <div className="row g-4">
              {sponsors.map((sponsor) => (
                <div key={sponsor.id} className="col-6 col-md-4 col-lg-3">
                  <div className="card sponsor-logo-card h-100 border-0 shadow-sm rounded-4 p-3 bg-white text-center d-flex flex-column justify-content-center align-items-center">
                    <div className="sponsor-img-container mb-3 d-flex align-items-center justify-content-center">
                      <img
                        src={sponsor.src}
                        alt={sponsor.name}
                        className="img-fluid sponsor-logo-img"
                        loading="lazy"
                      />
                    </div>
                    <span className="sponsor-name-caption text-uppercase fw-semibold text-muted small mt-auto">
                      {sponsor.name}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Support;
